import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MapPin, Truck, CreditCard } from "lucide-react";
import { api } from "@/lib/api";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/111a1161-c807-4877-8bbf-17ac6693744e/images/e64cab74ad77ef40febd689504d613a5170cfba80b98642c440139959b95c460.png";

const FIXER_IMG =
  "https://static.prod-images.emergentagent.com/jobs/111a1161-c807-4877-8bbf-17ac6693744e/images/aa2e66f627a78a41177b146aa29a92355ced4ad6b162ac9aadf7a8662b6363bd.png";

// Static visuals — labels are sourced dynamically from the catalogue
const CATEGORY_IMAGES = {
  iPhone:
    "https://images.unsplash.com/photo-1550041473-d296a3a8a18a?auto=format&fit=crop&w=900&q=80",
  iPad:
    "https://images.unsplash.com/photo-1561154464-82e9adf32764?auto=format&fit=crop&w=900&q=80",
  MacBook:
    "https://images.unsplash.com/photo-1511385278-5432b00777ed?auto=format&fit=crop&w=900&q=80",
  iMac:
    "https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?auto=format&fit=crop&w=900&q=80",
};

export default function Landing() {
  const [summary, setSummary] = useState([]);

  useEffect(() => {
    api.get("/categories-summary").then((r) => setSummary(r.data)).catch(() => setSummary([]));
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#E2E8F0]">
        <div
          className="absolute inset-0 opacity-60"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/60 to-white" />
        <div className="relative tk-container pt-20 pb-24 lg:pt-28 lg:pb-32">
          <p className="tk-overline mb-6" data-testid="hero-overline">Norges Apple-reparasjonsmarked</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-3xl leading-[1.05]">
            Kvalitetsdeler, lokale verksteder.<br />
            <span className="text-[#4A7C59]">Reparert i dag.</span>
          </h1>
          <p className="text-base text-slate-600 mt-6 max-w-xl leading-relaxed">
            Bestill kvalitetsdeler til Apple-enheter og book et lokalt Tekkdeler-verksted nær deg.
            Betal trygt med Vipps eller kort.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/butikk" className="tk-btn-primary" data-testid="hero-shop-btn">
              Finn reparasjonsdel <ArrowRight size={18} />
            </Link>
            <Link to="/verksted" className="tk-btn-secondary" data-testid="hero-fixer-btn">
              Finn verksted <MapPin size={18} />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
            {[
              { key: "garanti", icon: ShieldCheck, label: "12 mnd garanti" },
              { key: "frakt", icon: Truck, label: "Frakt eller innlevering" },
              { key: "vipps", icon: CreditCard, label: "Vipps & kort" },
            ].map((b) => (
              <div key={b.key} className="flex items-start gap-3" data-testid={`hero-badge-${b.key}`}>
                <div className="w-9 h-9 rounded-md bg-white border border-[#E2E8F0] flex items-center justify-center">
                  <b.icon size={18} className="text-[#0B1C30]" />
                </div>
                <span className="text-sm text-slate-700 mt-1">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="tk-container py-20 lg:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="tk-overline mb-3">Kategorier</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold max-w-xl leading-tight">
              Velg enhet — finn riktig del og book et verksted nær deg.
            </h2>
          </div>
          <Link to="/butikk" className="hidden md:inline-flex tk-btn-secondary" data-testid="all-categories-btn">
            Alle deler <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summary.length === 0 ? (
            <p className="col-span-full text-sm text-slate-500" data-testid="categories-loading">
              Laster kategorier …
            </p>
          ) : (
            summary.map((c) => (
              <Link
                key={c.category}
                to={`/butikk?kategori=${c.category}`}
                className="tk-card tk-card-interactive group block overflow-hidden !p-0"
                data-testid={`category-${c.category.toLowerCase()}`}
              >
                <div className="aspect-[4/3] overflow-hidden border-b border-[#E2E8F0]">
                  <img
                    src={CATEGORY_IMAGES[c.category]}
                    alt={c.category}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-1">{c.category}</h3>
                  <p
                    className="text-sm text-slate-500"
                    data-testid={`category-items-${c.category.toLowerCase()}`}
                  >
                    {c.items.join(" · ")}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm text-[#0B1C30] font-medium">
                    Se deler <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="tk-container py-20 lg:py-28">
          <p className="tk-overline mb-3">Slik fungerer det</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold max-w-xl leading-tight mb-12">
            Fra ødelagt til reparert på tre steg.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Velg del", d: "Bla i katalogen og velg riktig reparasjonsdel for din enhet." },
              { n: "02", t: "Velg verksted", d: "Finn ditt nærmeste Tekkdeler repair point basert på postnummer." },
              {
                n: "03",
                t: "Betal & følg opp",
                d: "Betal trygt og følg prosessen — se status i sanntid fra innlevering til ferdig reparasjon.",
              },
            ].map((s) => (
              <div key={s.n} className="tk-card" data-testid={`how-step-${s.n}`}>
                <p className="tk-overline mb-6 text-[#4A7C59]">{s.n}</p>
                <h3 className="text-xl font-semibold mb-3">{s.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FIXER CTA */}
      <section className="tk-container py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-md overflow-hidden border border-[#E2E8F0]">
            <img src={FIXER_IMG} alt="Verksted" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="tk-overline mb-3">For verksteder</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-6 leading-tight">
              Bli et Tekkdeler repair point.
            </h2>
            <p className="text-base text-slate-600 leading-relaxed max-w-lg">
              Få bestillinger fra hele Norge, kvalitetsdeler levert til døren, og en
              brand som kunder kan stole på. Vi tar oss av betaling, frakt og kundekommunikasjon.
            </p>
            <Link to="/bli-verksted" className="tk-btn-accent mt-8" data-testid="join-as-fixer-btn">
              Bli partner-verksted <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
